import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, ArrowRight, Trash2, Tag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import CartItem from '../components/CartItem';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CartPage = () => {
  const { items, clearCart, getSubtotal, currentTier, totalItems, itemsUntilNextTier, nextTierName, SEMI_GROS_MIN_TOTAL, GROS_MIN_TOTAL, forcedTier, wholesaleAllowed } = useCart();
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoError, setPromoError] = useState('');
  const [loadingPromo, setLoadingPromo] = useState(false);
  const navigate = useNavigate();
  const { t } = useLanguage();

  const subtotal = getSubtotal();
  const discount = promoApplied ? promoApplied.discount_amount : 0;
  const total = subtotal - discount;

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    
    setLoadingPromo(true);
    setPromoError('');
    
    try {
      const response = await axios.post(`${API_URL}/api/promo/validate`, {
        code: promoCode,
        total: subtotal
      });
      setPromoApplied(response.data);
      setPromoError('');
    } catch (error) {
      setPromoError(error.response?.data?.detail || 'Code promo invalide');
      setPromoApplied(null);
    } finally {
      setLoadingPromo(false);
    }
  };

  const removePromo = () => {
    setPromoApplied(null);
    setPromoCode('');
    setPromoError('');
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-main pt-24 pb-16 flex flex-col items-center justify-center">
        <ShoppingBag className="w-16 h-16 text-muted mb-4" />
        <h1 
          className="text-3xl text-heading font-light mb-4"
          style={{ fontFamily: 'Cormorant Garamond, serif' }}
        >
          {t('cart_empty')}
        </h1>
        <p className="text-muted mb-8">{t('cart_empty_desc')}</p>
        <Link to="/catalogue" className="btn-primary" data-testid="empty-cart-cta">
          {t('cart_view_catalogue')}
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-main pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-[color:var(--color-primary)] text-sm uppercase tracking-[0.3em] mb-2">{t('cart_title')}</p>
            <h1 
              className="text-4xl text-heading font-light"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {t('cart_your_selection')}
            </h1>
          </div>
          <button
            onClick={clearCart}
            className="flex items-center gap-2 text-muted hover:text-red-500 transition-colors"
            data-testid="clear-cart"
          >
            <Trash2 className="w-4 h-4" />
            {t('cart_clear')}
          </button>
        </div>

        {/* Pricing tier banner */}
        {items.length > 0 && forcedTier && (
          <div
            className="border p-4 flex items-center gap-3"
            style={{
              background: 'rgba(212, 175, 55, 0.08)',
              borderColor: 'rgba(212, 175, 55, 0.35)',
            }}
            data-testid="forced-tier-banner"
          >
            <Tag className="w-5 h-5 text-[color:var(--color-primary)] flex-shrink-0" />
            <div>
              <p className="text-sm text-[color:var(--color-primary)] font-medium">
                {forcedTier === 'vip'
                  ? '✨ Tarif VIP appliqué automatiquement'
                  : forcedTier === 'gros'
                    ? '🏷️ Tarif Gros à toutes quantités'
                    : '🏷️ Tarif Semi-gros à toutes quantités'}
              </p>
              <p className="text-xs text-body mt-1">
                Privilège accordé sur votre compte — peu importe le nombre d'articles.
              </p>
            </div>
          </div>
        )}
        {items.length > 0 && !forcedTier && !wholesaleAllowed && (
          <div className="bg-[color:rgb(var(--primary-rgb)/0.05)] border border-[color:rgb(var(--primary-rgb)/0.20)] p-4 flex items-start gap-3" data-testid="wholesale-locked-banner">
            <Tag className="w-5 h-5 text-[color:var(--color-primary)] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-[color:var(--color-primary)] font-medium">{t('product_pro_prices')}</p>
              <Link to="/inscription" className="text-sm text-body underline hover:text-[color:var(--color-primary)] transition-colors">
                {t('product_register_pro')}
              </Link>
            </div>
          </div>
        )}
        {items.length > 0 && !forcedTier && wholesaleAllowed && currentTier === 'detail' && (
          <div className="bg-[color:rgb(var(--primary-rgb)/0.05)] border border-[color:rgb(var(--primary-rgb)/0.20)] p-4 flex items-start gap-3" data-testid="tier-banner">
            <Tag className="w-5 h-5 text-[color:var(--color-primary)] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-[color:var(--color-primary)] font-medium">{t('cart_semi_gros_unlock')}</p>
              <p className="text-sm text-body mt-1">{t('cart_semi_gros_remaining', { count: itemsUntilNextTier })}</p>
              <div className="mt-2 h-1.5 bg-white/5 overflow-hidden">
                <div className="h-full bg-[color:var(--color-primary)] transition-all duration-500" style={{ width: `${Math.min(100, (totalItems / SEMI_GROS_MIN_TOTAL) * 100)}%` }} />
              </div>
            </div>
          </div>
        )}
        {items.length > 0 && !forcedTier && wholesaleAllowed && currentTier === 'semi_gros' && (
          <div className="space-y-2">
            <div className="bg-green-500/5 border border-green-500/20 p-4 flex items-center gap-3" data-testid="semi-gros-active">
              <Tag className="w-5 h-5 text-green-500 flex-shrink-0" />
              <p className="text-sm text-green-400">{t('cart_semi_gros_active')}</p>
            </div>
            <div className="bg-[color:rgb(var(--primary-rgb)/0.05)] border border-[color:rgb(var(--primary-rgb)/0.20)] p-4 flex items-start gap-3" data-testid="gros-push">
              <Tag className="w-5 h-5 text-[color:var(--color-primary)] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-[color:var(--color-primary)] font-medium">{t('cart_gros_unlock')}</p>
                <p className="text-sm text-body mt-1">{t('cart_gros_remaining', { count: itemsUntilNextTier })}</p>
                <div className="mt-2 h-1.5 bg-white/5 overflow-hidden">
                  <div className="h-full bg-[color:var(--color-primary)] transition-all duration-500" style={{ width: `${Math.min(100, (totalItems / GROS_MIN_TOTAL) * 100)}%` }} />
                </div>
              </div>
            </div>
          </div>
        )}
        {items.length > 0 && !forcedTier && currentTier === 'gros' && (
          <div className="bg-green-500/5 border border-green-500/20 p-4 flex items-center gap-3" data-testid="gros-active">
            <Tag className="w-5 h-5 text-green-500 flex-shrink-0" />
            <p className="text-sm text-green-400">{t('cart_gros_active')}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
            {items.map(item => (
              <CartItem key={item.product.id} item={item} />
            ))}
          </div>

          <div className="lg:col-span-1">
            <div className="card-surface p-6 sticky top-24">
              <h2 
                className="text-xl text-heading font-light mb-6"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                {t('cart_summary')}
              </h2>

              <div className="mb-6">
                <label className="input-label">{t('cart_promo_label')}</label>
                {promoApplied ? (
                  <div className="flex items-center justify-between bg-[color:rgb(var(--primary-rgb)/0.10)] border border-[color:rgb(var(--primary-rgb)/0.30)] p-3">
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-[color:var(--color-primary)]" />
                      <span className="text-[color:var(--color-primary)] text-sm">{promoApplied.code}</span>
                      <span className="text-muted text-sm">(-{promoApplied.discount_percent}%)</span>
                    </div>
                    <button
                      onClick={removePromo}
                      className="text-muted hover:text-heading transition-colors"
                      data-testid="remove-promo"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2 max-w-full overflow-hidden">
                    <input
                      type="text"
                      placeholder={t('cart_promo_placeholder')}
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value)}
                      className="flex-1 min-w-0 input-field text-sm"
                      data-testid="promo-input"
                    />
                    <button
                      onClick={handleApplyPromo}
                      disabled={loadingPromo || !promoCode.trim()}
                      className="btn-outline px-3 py-3 text-xs disabled:opacity-50 flex-shrink-0"
                      data-testid="apply-promo"
                    >
                      {loadingPromo ? '...' : 'OK'}
                    </button>
                  </div>
                )}
                {promoError && (
                  <p className="text-red-500 text-sm mt-2" data-testid="promo-error">{promoError}</p>
                )}
              </div>

              <div className="space-y-3 py-4 border-t border-theme">
                <div className="flex justify-between text-body">
                  <span>{t('cart_subtotal')}</span>
                  <span data-testid="subtotal">{subtotal.toFixed(2)}€</span>
                </div>
                {promoApplied && (
                  <div className="flex justify-between text-[color:var(--color-primary)]">
                    <span>{t('cart_discount')}</span>
                    <span data-testid="discount">-{discount.toFixed(2)}€</span>
                  </div>
                )}
              </div>

              <div className="flex justify-between items-center py-4 border-t border-theme">
                <span className="text-heading text-lg">{t('cart_total')}</span>
                <span 
                  className="text-2xl text-[color:var(--color-primary)] font-light"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  data-testid="total"
                >
                  {total.toFixed(2)}€
                </span>
              </div>

              <button
                onClick={() => navigate('/checkout', { state: { promoCode: promoApplied?.code } })}
                className="w-full btn-primary flex items-center justify-center gap-2 mt-4"
                data-testid="checkout-button"
              >
                {t('cart_checkout')}
                <ArrowRight className="w-4 h-4" />
              </button>

              <Link
                to="/catalogue"
                className="block text-center text-muted hover:text-heading transition-colors mt-4 text-sm"
              >
                {t('cart_continue_shopping')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
